/**
 * As consultas e as escritas do modulo 02, contra a API de verdade.
 *
 * `consultas.ts` continua existindo ao lado deste arquivo e continua servindo
 * FIXTURE — ele e a fonte do formulario de lancamento de combustivel, que
 * precisa de `horimetro` e `custoHora`, campos dos modulos 03 e 11 que o modelo
 * do modulo 02 nao tem. Trocar a fonte daquele hook quebraria a tela de outro
 * modulo, que nao e deste territorio. A fronteira esta em `docs/PENDENCIAS.md`.
 *
 * ## Invalidacao, e por que ela e grossa de proposito
 *
 * Toda escrita invalida `['frota']` inteiro, e nao so a linha alterada. O
 * motivo e o modelo de dados: mudar a situacao de uma maquina FECHA a vigencia
 * anterior e abre outra; alocar fecha a alocacao aberta. Uma escrita mexe em
 * duas linhas, e a lista mostra a corrente. Invalidacao cirurgica aqui seria
 * uma segunda copia da regra de vigencia, do lado do cliente, para envelhecer
 * sozinha.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatarAteDuasCasas, formatarFaixa } from '@/lib/formato';
import { retornoDe, semContexto, type AlvoDoRetorno, type ContextoDoRetorno } from '@/lib/retorno';
import { comVersao, ehSituacaoMudada } from '@/lib/versao-da-ficha';

import { ErroDaApi, paraNumero, paraTexto, pedir } from './api';
import {
  lerAbastecimentos,
  lerAlocacao,
  lerDocumentos,
  lerEquipamento,
  lerEvento,
  lerFoto,
  lerHoras,
  lerHorimetro,
  lerManutencao,
  lerCusto,
  lerCustoDoMes,
  lerOrdens,
  lerSituacao,
  type CadastrosDeApoio,
  type Combustivel,
  type Configuracao,
  type EquipamentoDaFrota,
  type Fabricante,
  type FotoDoEquipamento,
  type FotoNaLista,
  type Modelo,
  type Opcao,
  type Prontuario,
} from './frota';
import type { MaquinaARevisar, TipoDeEquipamento } from './tipos';

type Linha = Record<string, unknown>;

export const CHAVE_FROTA = ['frota'] as const;
const CHAVE_CATALOGO = ['frota', 'catalogo'] as const;
const CHAVE_APOIO = ['frota', 'apoio'] as const;
const CHAVE_TIPOS = ['frota', 'tipos'] as const;

/**
 * A frota inteira, com alocacao e situacao correntes. `ligada` falso nao pede:
 * a janela que so escolhe uma maquina pergunta quando abre, e so quem le.
 */
export function useFrota(ligada = true) {
  return useQuery({
    queryKey: CHAVE_FROTA,
    enabled: ligada,
    queryFn: async (): Promise<readonly EquipamentoDaFrota[]> => {
      const corpo = await pedir<{ equipamentos: Linha[] }>('/equipamentos');
      return corpo.equipamentos.map(lerEquipamento);
    },
  });
}

/** A ficha e a linha do tempo de uma maquina. */
export function useProntuario(id: string | null) {
  return useQuery({
    queryKey: ['frota', 'prontuario', id] as const,
    enabled: id !== null,
    queryFn: async (): Promise<Prontuario> => {
      const corpo = await pedir<{
        equipamento: Linha;
        alocacoes: Linha[];
        situacoes: Linha[];
        historico: Linha[];
        horimetro: Linha | null;
        leituras?: Linha[];
        horas: Linha;
        abastecimentos: Linha;
        documentos: Linha;
        manutencao?: Linha;
        ordens?: Linha;
        custo?: Linha;
        custoDoMes?: Linha;
      }>(`/equipamentos/${encodeURIComponent(id ?? '')}/prontuario`);

      return {
        equipamento: lerEquipamento(corpo.equipamento),
        alocacoes: corpo.alocacoes.map(lerAlocacao),
        situacoes: corpo.situacoes.map(lerSituacao),
        historico: corpo.historico.map(lerEvento),
        horimetro: lerHorimetro(corpo.horimetro),
        leituras: (corpo.leituras ?? []).map(lerHorimetro).filter((l): l is NonNullable<typeof l> => l !== null),
        horas: lerHoras(corpo.horas),
        abastecimentos: lerAbastecimentos(corpo.abastecimentos),
        documentos: lerDocumentos(corpo.documentos),
        manutencao: lerManutencao(corpo.manutencao),
        ordens: lerOrdens(corpo.ordens),
        custo: lerCusto(corpo.custo),
        custoDoMes: lerCustoDoMes(corpo.custoDoMes),
      };
    },
  });
}

function opcao(linha: Linha, campo: 'nome' | 'descricao' = 'nome'): Opcao {
  const codigo = typeof linha['codigo'] === 'string' ? linha['codigo'] : '';
  const nome = typeof linha[campo] === 'string' ? (linha[campo] as string) : '';
  return { id: String(linha['id'] ?? ''), rotulo: codigo ? `${codigo} · ${nome}` : nome };
}

/**
 * Os cadastros que os formularios precisam para montar as listas de escolha.
 *
 * A rota e provisoria e esta escrito que e: quando o modulo 01 publicar
 * `/cadastros/obras` e companhia, este hook passa a ler de la.
 */
/* `ligada` SEM valor padrão: quem chama diz a licença (`apoioLigado`), e
   esquecer vira erro de compilação, não um 403 na rede. */
export function useCadastrosDeApoio(ligada: boolean) {
  return useQuery({
    queryKey: CHAVE_APOIO,
    /* Só com a licença da ação e a da rota (`apoioLigado`): sem elas o
       formulário não aparece, e o pedido seria um 403 à toa. */
    enabled: ligada,
    queryFn: async (): Promise<CadastrosDeApoio> => {
      const corpo = await pedir<Record<string, Linha[]>>('/equipamentos/apoio/cadastros');
      const lista = (nome: string, campo: 'nome' | 'descricao' = 'nome') =>
        (corpo[nome] ?? []).map((linha) => opcao(linha, campo));

      return {
        /* A obra leva o CLIENTE dela: e o que o formulario de alocacao
           preenche sozinho ao escolher a obra. */
        obras: (corpo['obras'] ?? []).map((linha) => ({
          ...opcao(linha),
          clienteId: typeof linha['cliente_id'] === 'string' ? linha['cliente_id'] : null,
        })),
        clientes: lista('clientes'),
        centrosDeCusto: lista('centrosDeCusto'),
        contratos: (corpo['contratos'] ?? []).map((linha) => ({
          ...opcao(linha, 'descricao'),
          clienteId: typeof linha['cliente_id'] === 'string' ? linha['cliente_id'] : null,
        })),
        filiais: lista('filiais'),
        configuracoes: (corpo['configuracoes'] ?? []).map((linha) => ({
          id: String(linha['id'] ?? ''),
          rotulo: [linha['fabricante'], linha['modelo'], linha['nome']]
            .filter((parte): parte is string => typeof parte === 'string' && parte.length > 0)
            .join(' · '),
          /* O tipo e o medidor do modelo decidem o ícone da opção (`truck` ou `tractor`). */
          tipo: paraTexto(linha['tipo']),
          tipoMedidor: paraTexto(linha['tipo_medidor']),
          capacidadeTanqueL: paraNumero(linha['capacidade_tanque_l']),
          motorModelo: paraTexto(linha['motor_modelo']),
          fabricante: paraTexto(linha['fabricante']),
          modelo: paraTexto(linha['modelo']),
        })),
      };
    },
  });
}

/** Fabricante, modelo e configuracao — a hierarquia da ferramenta 1. `ligada` como em `useFrota`. */
export function useCatalogo(ligada = true) {
  return useQuery({
    queryKey: CHAVE_CATALOGO,
    enabled: ligada,
    queryFn: async () => {
      const [f, m, c] = await Promise.all([
        pedir<{ fabricantes: Linha[] }>('/catalogo/fabricantes'),
        pedir<{ modelos: Linha[] }>('/catalogo/modelos'),
        pedir<{ configuracoes: Linha[] }>('/catalogo/configuracoes'),
      ]);

      const fabricantes: Fabricante[] = f.fabricantes.map((linha) => ({
        id: String(linha['id'] ?? ''),
        nome: String(linha['nome'] ?? ''),
        ativo: linha['ativo'] !== false,
      }));

      const modelos: Modelo[] = m.modelos.map((linha) => ({
        id: String(linha['id'] ?? ''),
        nome: String(linha['nome'] ?? ''),
        ativo: linha['ativo'] !== false,
        tipo: String(linha['tipo'] ?? ''),
        fabricanteId: String(linha['fabricante_id'] ?? ''),
        fabricante: typeof linha['fabricante'] === 'string' ? linha['fabricante'] : null,
      }));

      const numero = (valor: unknown): number | null => {
        if (valor === null || valor === undefined || valor === '') return null;
        const n = Number(valor);
        return Number.isFinite(n) ? n : null;
      };

      const configuracoes: Configuracao[] = c.configuracoes.map((linha) => ({
        id: String(linha['id'] ?? ''),
        nome: String(linha['nome'] ?? ''),
        ativo: linha['ativo'] !== false,
        modeloId: String(linha['modelo_id'] ?? ''),
        modelo: typeof linha['modelo'] === 'string' ? linha['modelo'] : null,
        fabricante: typeof linha['fabricante'] === 'string' ? linha['fabricante'] : null,
        tipo: typeof linha['tipo'] === 'string' ? linha['tipo'] : null,
        tipoMedidor: linha['tipo_medidor'] === 'odometro' ? 'odometro' : 'horimetro',
        capacidadeTanqueL: numero(linha['capacidade_tanque_l']),
        consumoMinimo: numero(linha['consumo_minimo']),
        consumoMaximo: numero(linha['consumo_maximo']),
        potenciaKw: numero(linha['potencia_kw']),
      }));

      return { fabricantes, modelos, configuracoes };
    },
  });
}

/**
 * A lista de tipos de máquina da empresa (0131, decisão 57), na ordem da
 * planilha de importação. Muda raramente: fica fresca por dez minutos.
 */
export function useTiposDeEquipamento(ligada = true) {
  return useQuery({
    queryKey: CHAVE_TIPOS,
    enabled: ligada,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<readonly TipoDeEquipamento[]> => {
      const r = await pedir<{ tipos: Linha[] }>('/equipamentos/tipos');
      return r.tipos.map((linha) => ({
        id: String(linha['id'] ?? ''),
        nome: String(linha['nome'] ?? ''),
        prefixo: String(linha['prefixo'] ?? ''),
        ordem: Number(linha['ordem'] ?? 0),
      }));
    },
  });
}

/**
 * O código que a máquina deste tipo vai receber AGORA (0131). É prévia: o
 * número só é dela quando grava, e outro cadastro no meio pode pegá-lo. Mora
 * sob `['frota']`, então todo cadastro que grava a renova.
 */
export function useProximoCodigo(tipo: string, ligada = true) {
  const chave = tipo.trim();
  return useQuery({
    queryKey: ['frota', 'proximo-codigo', chave] as const,
    enabled: ligada && chave !== '',
    queryFn: async () => {
      const r = await pedir<{ codigo: string | null }>(`/equipamentos/proximo-codigo?tipo=${encodeURIComponent(chave)}`);
      return r.codigo ?? null;
    },
  });
}

/**
 * As máquinas que o dono pediu para apontar (0131): tipo fora da lista, ou
 * código com o prefixo de outro tipo. Mora sob `['frota']`: corrigir o tipo
 * na ficha tira a máquina daqui sozinho.
 */
export function useMaquinasARevisar(ligada = true) {
  return useQuery({
    queryKey: ['frota', 'a-revisar'] as const,
    enabled: ligada,
    queryFn: async (): Promise<readonly MaquinaARevisar[]> => {
      const r = await pedir<{ maquinas: Linha[] }>('/equipamentos/a-revisar');
      const texto = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
      return r.maquinas.map((linha) => ({
        id: String(linha['id'] ?? ''),
        codigo: String(linha['codigo'] ?? ''),
        tipo: texto(linha['tipo']),
        tipoDaLista: texto(linha['tipoDaLista']),
        prefixoDoTipo: texto(linha['prefixoDoTipo']),
        prefixoDoCodigo: texto(linha['prefixoDoCodigo']),
        tipoDoPrefixo: texto(linha['tipoDoPrefixo']),
        motivo: linha['motivo'] === 'codigo_de_outro_tipo' ? 'codigo_de_outro_tipo' : 'tipo_fora_da_lista',
      }));
    },
  });
}

function corpo(dados: unknown): RequestInit {
  return { method: 'POST', body: JSON.stringify(dados) };
}

/** Invalida a frota inteira depois de qualquer escrita. Ver o cabecalho. */
/**
 * A máquina como a lista da frota a mostra — o "em quê" do retorno (R04,
 * p.32). Vem do cache que a tela já leu: a resposta de várias escritas traz
 * só o id, e o toast não diz "o equipamento".
 */
function useMaquinaEmCache(): (id: string) => AlvoDoRetorno | undefined {
  const cliente = useQueryClient();
  return (id) => {
    const e = cliente.getQueryData<readonly EquipamentoDaFrota[]>(CHAVE_FROTA)?.find((x) => x.id === id);
    return e ? { codigo: e.codigo, nome: e.descricao } : undefined;
  };
}

function quantos(n: number, um: string, varios: string): string {
  return `${String(n)} ${n === 1 ? um : varios}`;
}

/** A ficha que a escrita devolve (a seleção da rota), como alvo. */
function alvoDaFicha(r: Record<string, unknown>): AlvoDoRetorno {
  return { codigo: String(r['codigo'] ?? ''), nome: String(r['descricao'] ?? '') };
}

function useAoGravar(chaves: readonly (readonly unknown[])[] = [CHAVE_FROTA]) {
  const cliente = useQueryClient();
  return () => {
    for (const chave of chaves) void cliente.invalidateQueries({ queryKey: chave });
  };
}

/**
 * O cadastro de uma máquina. Sem `codigo` desde a 0131 (decisão 57): o código
 * é do sistema, criado pelo tipo e pela sequência quando a máquina grava.
 */
export interface PedidoDeEquipamento {
  readonly descricao: string;
  readonly configuracaoId: string | null;
  readonly filialId: string | null;
  readonly numeroSerie: string | null;
  readonly anoFabricacao: number | null;
  readonly tipoMedidor: 'horimetro' | 'odometro' | null;
  /** Um tipo da LISTA (0131); nulo herda o tipo do MODELO (0124). O código nasce dele. */
  readonly tipo: string | null;
  readonly capacidadeTanqueL: number | null;
  /** Nulo herda da configuracao; preenchido significa "esta com outro motor". */
  readonly motorModelo: string | null;
  /** Sempre da unidade: identifica a peca, e nao o modelo. */
  readonly motorNumeroSerie: string | null;
  /* 0060. Placa e Renavam ja vao normalizados (`ficha.ts`). */
  readonly patrimonio: string | null;
  /** 0125 — ja normalizado pela tela: caixa alta, sem espaco. */
  readonly codigoInterno: string | null;
  readonly combustivel: Combustivel | null;
  /** 0133: a maquina abastece em posto parceiro. Nulo nasce desligada. */
  readonly abasteceEmPosto: boolean;
  readonly anoModelo: number | null;
  readonly anoAquisicao: number | null;
  readonly placa: string | null;
  readonly renavam: string | null;
  /**
   * A leitura do medidor no dia do cadastro. Nao e coluna da ficha: o servidor
   * a grava como leitura de ajuste, na mesma transacao.
   */
  readonly leituraInicial?: number | null;
}

/** Os campos que o ajuste da ficha sabe apagar — a lista do servidor. */
export type CampoQueSeApaga =
  | 'configuracaoId'
  | 'filialId'
  | 'numeroSerie'
  | 'anoFabricacao'
  | 'tipoMedidor'
  | 'tipo'
  | 'capacidadeTanqueL'
  | 'motorModelo'
  | 'motorNumeroSerie'
  | 'patrimonio'
  | 'codigoInterno'
  | 'combustivel'
  | 'anoModelo'
  | 'anoAquisicao'
  | 'placa'
  | 'renavam';

/**
 * O ajuste da ficha. Cada campo vem PREENCHIDO ou em `limpar`: o `PATCH` usa
 * `coalesce`, e nulo sozinho nao apaga nada.
 */
export interface AjusteDaFicha {
  /* Sem `codigo` (0131): o ajuste nunca muda o código da máquina. */
  readonly descricao: string;
  readonly configuracaoId?: string;
  readonly filialId?: string;
  readonly numeroSerie?: string;
  readonly anoFabricacao?: number;
  readonly tipoMedidor?: 'horimetro' | 'odometro';
  /** 0124: vazio volta a herdar o tipo do modelo. 0131: só um tipo da lista. */
  readonly tipo?: string;
  readonly capacidadeTanqueL?: number;
  readonly motorModelo?: string;
  readonly motorNumeroSerie?: string;
  readonly patrimonio?: string;
  readonly codigoInterno?: string;
  readonly combustivel?: Combustivel;
  /** 0133: vai SEMPRE, ligado ou desligado — nao e campo que se apaga. */
  readonly abasteceEmPosto: boolean;
  readonly anoModelo?: number;
  readonly anoAquisicao?: number;
  readonly placa?: string;
  readonly renavam?: string;
  readonly limpar: readonly CampoQueSeApaga[];
}

/**
 * A ficha como esta no servidor AGORA, sem passar pelo cache: a tela de editar
 * a usa quando o servidor recusa o formulario por versao velha
 * (`ficha_alterada`), para refazer o formulario sobre a versao nova.
 */
export async function lerFichaAtual(id: string): Promise<EquipamentoDaFrota> {
  return lerEquipamento(await pedir<Record<string, unknown>>(`/equipamentos/${encodeURIComponent(id)}`));
}

export function useAjustarFicha() {
  const aoGravar = useAoGravar();
  return useMutation({
    meta: {
      retorno: retornoDe<Record<string, unknown>, { ajuste: AjusteDaFicha }>((r, v) => ({
        titulo: 'Ficha alterada',
        alvo: alvoDaFicha(r),
        fatos: [
          quantos(Object.keys(v.ajuste).filter((k) => k !== 'limpar').length, 'campo alterado', 'campos alterados'),
          /* "do catálogo", e nao "da configuração": desde a 0124 o `tipo`
             tambem volta a herdar, e ele herda do MODELO. Catálogo e o nome
             que a tela da a hierarquia fabricante > modelo > configuração. */
          v.ajuste.limpar.length > 0 ? quantos(v.ajuste.limpar.length, 'campo voltou', 'campos voltaram') + ' a herdar do catálogo' : null,
        ],
        efeito: 'A alteração ficou na trilha, com quem mudou e quando',
      })),
    },
    /* `versao`: a da ficha QUANDO O FORMULARIO ABRIU (lib/versao-da-ficha.ts).
       Com ela o servidor recusa o formulario velho em vez de gravar por cima
       do que outra pessoa gravou depois (robos de 20/10/2026). */
    mutationFn: ({ id, ajuste, versao }: { id: string; ajuste: AjusteDaFicha; versao: string | null }) =>
      pedir<Record<string, unknown>>(`/equipamentos/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(comVersao(ajuste, versao)),
      }),
    onSuccess: aoGravar,
  });
}

/**
 * A leitura do medidor registrada pela ficha — a primeira, ou a correcao.
 * O prontuario mora sob `['frota']`, entao a invalidacao grossa o alcanca.
 */
export function useRegistrarLeitura() {
  const aoGravar = useAoGravar();
  const maquina = useMaquinaEmCache();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { id: string; valor: number; motivo: string }>((_r, v) => {
        const alvo = maquina(v.id);
        return {
          titulo: 'Leitura do medidor registrada',
          ...(alvo ? { alvo } : {}),
          fatos: [formatarAteDuasCasas(v.valor), `motivo: ${v.motivo}`],
        };
      }),
    },
    mutationFn: ({ id, valor, motivo }: { id: string; valor: number; motivo: string }) =>
      pedir<Record<string, unknown>>(`/equipamentos/${encodeURIComponent(id)}/leituras`, corpo({ valor, motivo })),
    onSuccess: aoGravar,
  });
}

export function useCadastrarEquipamento() {
  const aoGravar = useAoGravar();
  return useMutation({
    meta: {
      retorno: retornoDe<Record<string, unknown>>((r) => ({
        titulo: 'Equipamento cadastrado',
        alvo: alvoDaFicha(r),
        fatos: [
          [r['fabricante'], r['modelo']].filter(Boolean).map(String).join(' ') || null,
          r['numero_serie'] ? `série ${String(r['numero_serie'])}` : null,
          r['filial'] ? String(r['filial']) : null,
        ],
        efeito: 'Já está na lista da frota',
      })),
    },
    mutationFn: (pedido: PedidoDeEquipamento) =>
      pedir<Record<string, unknown>>('/equipamentos', corpo(pedido)),
    onSuccess: aoGravar,
  });
}

/**
 * A faixa de consumo da unidade (decisao 8). Nulo nos dois volta a herdar da
 * configuracao — por isso e PUT com os dois, e nao o PATCH, que nao sabe limpar.
 */
export function useGravarFaixa() {
  const aoGravar = useAoGravar();
  return useMutation({
    meta: {
      retorno: retornoDe<Record<string, unknown>, { minimo: number | null; maximo: number | null }>((r, v) => {
        const faixa = formatarFaixa(v.minimo, v.maximo);
        const unidade = r['tipo_medidor'] === 'odometro' ? 'L/km' : 'L/h';
        return {
          titulo: 'Faixa de consumo gravada',
          alvo: alvoDaFicha(r),
          fatos: [faixa === null ? 'Volta a herdar da configuração' : `${faixa} ${unidade}`],
        };
      }),
    },
    mutationFn: ({ id, minimo, maximo }: { id: string; minimo: number | null; maximo: number | null }) =>
      pedir<Record<string, unknown>>(`/equipamentos/${encodeURIComponent(id)}/faixa-de-consumo`, {
        method: 'PUT',
        body: JSON.stringify({ minimo, maximo }),
      }),
    onSuccess: aoGravar,
  });
}

export function useDesativarEquipamento() {
  const aoGravar = useAoGravar();
  return useMutation({
    meta: {
      retorno: retornoDe<Record<string, unknown>>((r) => ({
        titulo: 'Equipamento desativado',
        alvo: alvoDaFicha(r),
        efeito: 'Saiu da frota ativa; o histórico continua no prontuário',
      })),
    },
    mutationFn: (id: string) =>
      pedir<Record<string, unknown>>(`/equipamentos/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ ativo: false }),
      }),
    onSuccess: aoGravar,
  });
}

/**
 * REATIVAR A MAQUINA DESATIVADA (31/10/2026). A desativada sai das listas de
 * escolha (alocacao, abastecimento, posto parceiro), e a tela nao dizia que
 * ela estava desativada nem tinha como voltar: o dono via a ficha em
 * "Operando" e a maquina sumida do lancamento do posto.
 */
export function useReativarEquipamento() {
  const aoGravar = useAoGravar();
  return useMutation({
    meta: {
      retorno: retornoDe<Record<string, unknown>>((r) => ({
        titulo: 'Equipamento reativado',
        alvo: alvoDaFicha(r),
        efeito: 'Voltou para as listas de escolha de alocação e de abastecimento',
      })),
    },
    mutationFn: (id: string) =>
      pedir<Record<string, unknown>>(`/equipamentos/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ ativo: true }),
      }),
    onSuccess: aoGravar,
  });
}

export interface PedidoDeAlocacao {
  readonly equipamentoId: string;
  readonly obraId: string;
  readonly clienteId: string | null;
  readonly contratoId: string | null;
  readonly centroCustoId: string | null;
  readonly motivo: string | null;
}

export function useAlocar() {
  const cliente = useQueryClient();
  const maquina = useMaquinaEmCache();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, PedidoDeAlocacao & { contexto?: ContextoDoRetorno }>((_r, v) => {
        const alvo = maquina(v.equipamentoId);
        return {
          titulo: 'Equipamento alocado',
          ...(alvo ? { alvo } : {}),
          fatos: [...(v.contexto?.fatos ?? []), v.motivo ? `motivo: ${v.motivo}` : null],
        };
      }),
    },
    mutationFn: ({ equipamentoId, ...resto }: PedidoDeAlocacao & { contexto?: ContextoDoRetorno }) =>
      pedir<Record<string, unknown>>(
        `/equipamentos/${encodeURIComponent(equipamentoId)}/alocacoes`,
        corpo(semContexto(resto)),
      ),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_FROTA });
    },
  });
}

export function useEncerrarAlocacao() {
  const cliente = useQueryClient();
  const maquina = useMaquinaEmCache();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { equipamentoId: string; motivo: string | null; obra?: string | null }>((_r, v) => {
        const alvo = maquina(v.equipamentoId);
        return {
          titulo: 'Alocação encerrada',
          ...(alvo ? { alvo } : {}),
          fatos: [v.obra ? `saiu da ${v.obra}` : null, v.motivo ? `motivo: ${v.motivo}` : null],
          efeito: 'A máquina ficou sem obra',
        };
      }),
    },
    mutationFn: ({
      equipamentoId,
      alocacaoId,
      motivo,
    }: {
      equipamentoId: string;
      alocacaoId: string;
      motivo: string | null;
      obra?: string | null;
    }) =>
      pedir<Record<string, unknown>>(
        `/equipamentos/${encodeURIComponent(equipamentoId)}/alocacoes/${encodeURIComponent(alocacaoId)}/encerramento`,
        corpo({ motivo }),
      ),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_FROTA });
    },
  });
}

export function useMudarSituacao() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({
      equipamentoId,
      status,
      motivo,
      motivoId,
      situacaoVista,
    }: {
      equipamentoId: string;
      status: string;
      motivo: string;
      /* A CAUSA CATALOGADA. Obrigatoria em Parada e Em manutencao desde a
         migracao 0014 — e o que permite responder "o que mais tira
         disponibilidade", que texto livre nao responde. Nula nos outros quatro
         status: pedir causa de parada para a maquina estar operando obrigaria
         a inventar uma. */
      motivoId?: string | null;
      /* A SITUACAO QUE O FORMULARIO VIU AO ABRIR (robos de 20/10/2026, achado
         10): o id da vigente, ou nulo sem situacao. O servidor recusa com
         `situacao_mudou` se outra pessoa mudou a situacao nesse meio tempo. */
      situacaoVista: string | null;
    }) =>
      pedir<Record<string, unknown>>(
        `/equipamentos/${encodeURIComponent(equipamentoId)}/situacoes`,
        corpo({ status, motivo, motivoId: motivoId ?? null, situacaoVista }),
      ),
    /* O toast sai da tela, que sabe a máquina e o status: "Situação registrada
       em ESC-001: Parada." diz mais que o "Registro gravado." genérico. */
    meta: { silencioso: true },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_FROTA });
    },
    /* Recusada porque a situacao mudou: a ficha passa a mostrar a de agora. */
    onError: (erro) => {
      if (ehSituacaoMudada(erro)) void cliente.invalidateQueries({ queryKey: CHAVE_FROTA });
    },
  });
}

export function useCadastrarFabricante() {
  const aoGravar = useAoGravar([CHAVE_CATALOGO, CHAVE_APOIO]);
  return useMutation({
    meta: {
      retorno: retornoDe<Record<string, unknown>>((r) => ({
        titulo: 'Fabricante cadastrado',
        alvo: { nome: String(r['nome'] ?? '') },
        efeito: 'Já aparece na escolha do modelo',
      })),
    },
    mutationFn: (nome: string) => pedir<Record<string, unknown>>('/catalogo/fabricantes', corpo({ nome })),
    onSuccess: aoGravar,
  });
}

export function useCadastrarModelo() {
  const aoGravar = useAoGravar([CHAVE_CATALOGO, CHAVE_APOIO]);
  return useMutation({
    meta: {
      retorno: retornoDe<Record<string, unknown>>((r) => ({
        titulo: 'Modelo cadastrado',
        alvo: { nome: String(r['nome'] ?? '') },
        fatos: [r['tipo'] ? String(r['tipo']) : null],
      })),
    },
    mutationFn: (pedido: { fabricanteId: string; nome: string; tipo: string }) =>
      pedir<Record<string, unknown>>('/catalogo/modelos', corpo(pedido)),
    onSuccess: aoGravar,
  });
}

export interface PedidoDeConfiguracao {
  readonly modeloId: string;
  readonly nome: string;
  readonly tipoMedidor: 'horimetro' | 'odometro';
  readonly capacidadeTanqueL: number | null;
  readonly consumoMinimo: number | null;
  readonly consumoMaximo: number | null;
  readonly potenciaKw: number | null;
  /** O motor de fabrica desta configuracao. A unidade herda. */
  readonly motorModelo: string | null;
}

export function useCadastrarConfiguracao() {
  const aoGravar = useAoGravar([CHAVE_CATALOGO, CHAVE_APOIO, CHAVE_FROTA]);
  return useMutation({
    meta: {
      retorno: retornoDe<Record<string, unknown>, PedidoDeConfiguracao>((r, v) => ({
        titulo: 'Configuração cadastrada',
        alvo: { nome: String(r['nome'] ?? v.nome) },
        fatos: [
          v.tipoMedidor === 'odometro' ? 'odômetro' : 'horímetro',
          v.capacidadeTanqueL === null ? null : `tanque de ${formatarAteDuasCasas(v.capacidadeTanqueL)} L`,
          formatarFaixa(v.consumoMinimo, v.consumoMaximo),
        ],
      })),
    },
    mutationFn: (pedido: PedidoDeConfiguracao) =>
      pedir<Record<string, unknown>>('/catalogo/configuracoes', corpo(pedido)),
    onSuccess: aoGravar,
  });
}

/* ---------------------------------------------------------------------------
 * A foto do equipamento (ADR-007).
 *
 * Tres escritas e uma leitura, todas pela mesma porta (`pedir`): a foto e a
 * miniatura sobem como CORPO CRU, um arquivo por pedido, com
 * `content-type: image/jpeg` — o servidor le o tipo pelos bytes e ignora o
 * cabecalho, mas o `pedir` poria `application/json` num corpo que nao e.
 * ------------------------------------------------------------------------- */

const CABECALHO_JPEG = { 'content-type': 'image/jpeg' } as const;

function caminhoDaFoto(equipamentoId: string): string {
  return `/equipamentos/${encodeURIComponent(equipamentoId)}/foto`;
}

/** A foto inteira, com as URLs assinadas de 5 minutos. */
export interface FotoComEndereco {
  readonly foto: FotoDoEquipamento;
  readonly url: string;
  readonly miniaturaUrl: string | null;
  readonly expiraEmSegundos: number;
}

function incompleta(): ErroDaApi {
  return new ErroDaApi(
    502,
    'a resposta da foto chegou incompleta',
    'Carregue a foto de novo; se continuar, avise o suporte com o horário em que aconteceu.',
  );
}

async function buscarFoto(equipamentoId: string): Promise<FotoComEndereco> {
  const corpo = await pedir<Linha>(caminhoDaFoto(equipamentoId));
  const foto = lerFoto(corpo['foto']);
  const url = paraTexto(corpo['url']);
  if (!foto || !url) throw incompleta();
  return {
    foto,
    url,
    miniaturaUrl: paraTexto(corpo['miniaturaUrl']),
    expiraEmSegundos: paraNumero(corpo['expiraEmSegundos']) ?? 300,
  };
}

/**
 * A foto que a ficha mostra.
 *
 * A chave leva o ID DA FOTO, alem do da maquina: trocar a foto e outra
 * consulta, e a ficha nao mostra a foto anterior enquanto a nova carrega.
 * `staleTime` de 4 minutos fica abaixo dos 5 da URL assinada — passado isso,
 * voltar a aba pede URL nova antes de a velha vencer.
 */
export function useFotoDoEquipamento(equipamentoId: string, fotoId: string | null) {
  return useQuery({
    queryKey: ['frota', 'foto', equipamentoId, fotoId] as const,
    enabled: fotoId !== null,
    staleTime: 4 * 60 * 1000,
    queryFn: () => buscarFoto(equipamentoId),
  });
}

/**
 * Poe a foto nova (ou a ausencia dela) na lista JA EM CACHE, antes de a lista
 * voltar do servidor. Nao substitui a invalidacao grossa do cabecalho — ela
 * vem logo depois e traz a miniatura assinada. Existe para a ficha nao
 * mostrar, no intervalo, a foto que acabou de sair.
 */
function useFotoNaListaEmCache() {
  const cliente = useQueryClient();
  return (equipamentoId: string, foto: FotoNaLista | null) => {
    cliente.setQueryData<readonly EquipamentoDaFrota[]>(CHAVE_FROTA, (lista) =>
      lista?.map((e) => (e.id === equipamentoId ? { ...e, foto } : e)),
    );
  };
}

export interface FotoEnviada {
  readonly foto: FotoDoEquipamento;
  /** A foto ficou salva, mas a miniatura nao subiu nem na segunda tentativa. */
  readonly miniaturaFalhou: boolean;
}

/**
 * Sobe a foto preparada e, em seguida, a miniatura dela.
 *
 * Dois pedidos, na ordem: a miniatura nomeia a foto (`fotoId`) de que ela e
 * miniatura. Se a segunda falhar, tenta mais UMA vez; falhando de novo, a foto
 * continua salva e a tela avisa — reenviar a mesma foto devolve 200 com a foto
 * atual, e a miniatura sobe entao.
 */
export function useEnviarFoto() {
  const aoGravar = useAoGravar();
  const naLista = useFotoNaListaEmCache();
  const maquina = useMaquinaEmCache();
  return useMutation({
    meta: {
      retorno: retornoDe<FotoEnviada, { equipamentoId: string }>((r, v) => {
        const alvo = maquina(v.equipamentoId);
        return {
          titulo: 'Foto da máquina trocada',
          tom: r.miniaturaFalhou ? 'ressalva' : 'sucesso',
          ...(alvo ? { alvo } : {}),
          fatos: [r.miniaturaFalhou ? 'A miniatura não subiu; envie a mesma foto de novo para completar' : null],
          efeito: 'A foto anterior fica na linha do tempo',
        };
      }),
    },
    mutationFn: async ({
      equipamentoId,
      preparada,
    }: {
      equipamentoId: string;
      preparada: { readonly principal: Blob; readonly miniatura: Blob };
    }): Promise<FotoEnviada> => {
      const caminho = caminhoDaFoto(equipamentoId);
      const enviada = await pedir<Linha>(caminho, { method: 'PUT', body: preparada.principal, headers: CABECALHO_JPEG });
      const foto = lerFoto(enviada['foto']);
      if (!foto) throw incompleta();

      for (let tentativa = 0; tentativa < 2; tentativa += 1) {
        try {
          const comMiniatura = await pedir<Linha>(`${caminho}/miniatura?fotoId=${encodeURIComponent(foto.id)}`, {
            method: 'PUT',
            body: preparada.miniatura,
            headers: CABECALHO_JPEG,
          });
          return { foto: lerFoto(comMiniatura['foto']) ?? foto, miniaturaFalhou: false };
        } catch {
          /* a foto ja esta salva: tenta a miniatura mais uma vez */
        }
      }
      return { foto, miniaturaFalhou: true };
    },
    onSuccess: (enviada, { equipamentoId }) => {
      naLista(equipamentoId, { id: enviada.foto.id, miniaturaUrl: null, enviadoEm: enviada.foto.enviadoEm });
      aoGravar();
    },
  });
}

/** Remocao LOGICA: a foto sai da ficha e fica na linha do tempo. */
export function useRemoverFoto() {
  const aoGravar = useAoGravar();
  const naLista = useFotoNaListaEmCache();
  const maquina = useMaquinaEmCache();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, string>((_r, id) => {
        const alvo = maquina(id);
        return {
          titulo: 'Foto da máquina removida',
          ...(alvo ? { alvo } : {}),
          efeito: 'A foto sai da ficha e fica na linha do tempo',
        };
      }),
    },
    mutationFn: (equipamentoId: string) => pedir<Linha>(caminhoDaFoto(equipamentoId), { method: 'DELETE' }),
    onSuccess: (_resposta, equipamentoId) => {
      naLista(equipamentoId, null);
      aoGravar();
    },
  });
}

/**
 * Abre a foto inteira numa aba nova, pela URL assinada. A aba abre ANTES do
 * pedido, no clique: aberta depois de um `await`, o navegador a trataria como
 * janela nao pedida e a bloquearia. O mesmo jeito de `abrirAnexo` da O.S.
 */
export async function abrirFoto(equipamentoId: string): Promise<void> {
  const aba = window.open('about:blank', '_blank');
  try {
    const { url } = await buscarFoto(equipamentoId);
    if (aba) {
      aba.opener = null;
      aba.location.href = url;
    } else {
      window.location.assign(url);
    }
  } catch (erro) {
    aba?.close();
    throw erro;
  }
}
